import { IF, UIDialogView, UINativeScrollView, UITextView, UIView, YES } from "uicore-ts"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHTextArea } from "../../Custom components/RH/RHTextArea"
import { RHDialogs } from "../../Custom components/RH/Utils_slop/RHDesignTokens"


// ─────────────────────────────────────────────────────────────────────────────
// "Tulemi põhjendus" — a small modal for adding / editing the result note that
// the procurer attaches to a single part offer during evaluation. The text is
// the offer's `evaluationNotes` field: nothing changes in the data model, this
// dialog simply replaces the old inline textarea in the offers table with a
// focused editor opened from the "Tulem" column button.
// ─────────────────────────────────────────────────────────────────────────────

export class EvaluationNotesDialogView extends UIDialogView {

    readonly contentView = new UINativeScrollView()
        .configuredWithObject({
            backgroundColor: RHDialogs.evaluationNotesDialogBackgroundColor,
            style: {
                borderRadius: RHDialogs.evaluationNotesDialogBorderRadius,
                boxShadow: RHDialogs.evaluationNotesDialogBoxShadow
            }
        })
        .addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(undefined, RHDialogs.evaluationNotesTitleTextViewType)
        .configuredWithObject({
            text: RHDialogs.evaluationNotesTitleText,
            textColor: RHDialogs.evaluationNotesTitleTextColor,
            style: {
                fontFamily: RHDialogs.evaluationNotesTitleFontFamily,
                fontSize: RHDialogs.evaluationNotesTitleFontSize,
                fontWeight: RHDialogs.evaluationNotesTitleFontWeight,
                lineHeight: RHDialogs.evaluationNotesTitleLineHeight
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly notesTextArea = new RHTextArea()
        .configuredWithObject({
            placeholderText: RHDialogs.evaluationNotesTextAreaPlaceholder
        })
        .addedAsSubviewToView(this.contentView)

    readonly deleteButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: RHDialogs.evaluationNotesDeleteButtonTitle },
            variant: RHDialogs.evaluationNotesDeleteButtonVariant,
            contentPadding: RHDialogs.evaluationNotesFooterButtonContentPadding,
            style: {
                fontSize: RHDialogs.evaluationNotesFooterButtonFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._commitAndDismiss("")
                }
            }
        })

    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: RHDialogs.evaluationNotesCancelButtonTitle },
            variant: RHDialogs.evaluationNotesCancelButtonVariant,
            contentPadding: RHDialogs.evaluationNotesFooterButtonContentPadding,
            style: {
                fontSize: RHDialogs.evaluationNotesFooterButtonFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.dismiss()
                }
            }
        })

    readonly saveButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: RHDialogs.evaluationNotesSaveButtonTitle },
            variant: RHDialogs.evaluationNotesSaveButtonVariant,
            contentPadding: RHDialogs.evaluationNotesFooterButtonContentPadding,
            style: {
                fontSize: RHDialogs.evaluationNotesFooterButtonFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._commitAndDismiss(this.notesTextArea.text)
                }
            }
        })

    readonly closeButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: RHDialogs.evaluationNotesCloseButtonTitle },
            variant: RHDialogs.evaluationNotesCloseButtonVariant,
            contentPadding: RHDialogs.evaluationNotesFooterButtonContentPadding,
            style: {
                fontSize: RHDialogs.evaluationNotesFooterButtonFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.dismiss()
                }
            }
        })

    readonly _bottomView = new UIView().addedAsSubviewToView(this.contentView)

    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = []
        if (this.notesTextArea.enabled && this.notesTextArea.superview === this.contentView) {
            items.push(this.notesTextArea)
        }
        if (this.deleteButton.superview === this.contentView) {
            items.push(this.deleteButton)
        }
        if (this.cancelButton.superview === this.contentView) {
            items.push(this.cancelButton)
        }
        if (this.saveButton.superview === this.contentView) {
            items.push(this.saveButton)
        }
        if (this.closeButton.superview === this.contentView) {
            items.push(this.closeButton)
        }
        return items
    })

    private _isEditable = YES
    private _didCommitNotes?: (notes: string) => void

    readonly _escKeyHandler = (event: KeyboardEvent) => {
        // Yield to any CBDialogViewShower dialog open on top of this one — its
        // capture-phase trap owns the keyboard while active.
        if (CBDialogViewShower.currentDialogViewShower) {
            return
        }
        if (event.key === "Escape") {
            this.dismiss()
        }
    }


    constructor(elementID?: string) {
        super(elementID)

        // Adopt the content view as this dialog's view, otherwise only the dark
        // backdrop renders and the content is orphaned.
        this.view = this.contentView
        this.dismissesOnTapOutside = YES

        this.notesTextArea.controlEventTargetAccumulator.EnterDown = (_sender, event) => {
            if (
                this._isEditable &&
                event instanceof KeyboardEvent &&
                (event.ctrlKey || event.metaKey)
            ) {
                event.preventDefault()
                this._commitAndDismiss(this.notesTextArea.text)
            }
        }
    }


    override showInRootView(animated: boolean) {
        super.showInRootView(animated)
        document.body.addEventListener("keydown", this._escKeyHandler)
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager)
    }

    override dismiss(animated?: boolean) {
        document.body.removeEventListener("keydown", this._escKeyHandler)
        RHPageFocusManager.shared.popLayer()
        super.dismiss(animated)
    }


    /**
     * Opens the editor for one part offer's result note.
     * `didCommitNotes` is invoked with the new note text when the procurer saves
     * (or with an empty string when they delete it); it is NOT called when they
     * cancel / close / tap outside. The caller persists the value and refreshes
     * its table — this dialog only edits the text.
     */
    showForNotes(initialNotes: string, editable: boolean, didCommitNotes: (notes: string) => void) {
        this._isEditable = editable
        this._didCommitNotes = didCommitNotes

        this.notesTextArea.configureWithObject({
            text: initialNotes,
            enabled: editable,
            placeholderText: IF(editable)(() =>
                RHDialogs.evaluationNotesTextAreaPlaceholder
            ).ELSE(() => "Tulemi põhjendus puudub")
        })

        if (editable) {
            this.closeButton.removeFromSuperview()
            this.deleteButton.addedAsSubviewToView(this.contentView)
            this.cancelButton.addedAsSubviewToView(this.contentView)
            this.saveButton.addedAsSubviewToView(this.contentView)
        }
        else {
            this.deleteButton.removeFromSuperview()
            this.cancelButton.removeFromSuperview()
            this.saveButton.removeFromSuperview()
            this.closeButton.addedAsSubviewToView(this.contentView)
        }

        this.setNeedsLayout()
        this.showInRootView(YES)
    }

    override willAppear(animated?: boolean) {
        super.willAppear(animated)
        if (this._isEditable) {
            this.notesTextArea.focus()
        }
    }


    private _commitAndDismiss(notes: string) {
        this._didCommitNotes?.(notes)
        this.dismiss()
    }


    private _footerButtonWidth(button: RHButton): number {
        return Math.min(
            RHDialogs.evaluationNotesFooterButtonMaxWidth,
            Math.max(
                RHDialogs.evaluationNotesFooterButtonMinWidth,
                button.titleLabel.intrinsicContentWidth(RHDialogs.evaluationNotesFooterButtonHeight) +
                RHDialogs.evaluationNotesFooterButtonContentPadding * 2 +
                RHDialogs.evaluationNotesFooterButtonTextSafetyBuffer
            )
        )
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const dialogBounds = this.bounds
        const dialogWidth = Math.min(
            RHDialogs.evaluationNotesDialogMaxWidth,
            dialogBounds.width * RHDialogs.evaluationNotesDialogWidthViewportRatio
        )

        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(0)

        const bounds = this.contentView.contentBoundsWithInset(RHDialogs.evaluationNotesContentInset)

        const footerRowFrame = bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(
                RHDialogs.evaluationNotesTitleBottomGap,
                RHDialogs.evaluationNotesTextAreaHeight
            )
            .assignedAsFrameOfView(this.notesTextArea)
            .rectangleForNextRow(
                RHDialogs.evaluationNotesFooterButtonsTopGap,
                RHDialogs.evaluationNotesFooterButtonHeight
            )

        if (this._isEditable) {
            const deleteButtonWidth = this._footerButtonWidth(this.deleteButton)
            const cancelButtonWidth = this._footerButtonWidth(this.cancelButton)
            const saveButtonWidth = this._footerButtonWidth(this.saveButton)
            const footerRowWidth = deleteButtonWidth + cancelButtonWidth + saveButtonWidth +
                RHDialogs.evaluationNotesFooterButtonGap * 2

            footerRowFrame
                .rectangleWithWidth(footerRowWidth, 0.5)
                .distributeViewsAlongWidth(
                    [this.deleteButton, this.cancelButton, this.saveButton],
                    undefined,
                    RHDialogs.evaluationNotesFooterButtonGap,
                    [deleteButtonWidth, cancelButtonWidth, saveButtonWidth]
                )
        }
        else {
            footerRowFrame
                .rectangleWithWidth(this._footerButtonWidth(this.closeButton), 0.5)
                .assignedAsFrameOfView(this.closeButton)
        }

        footerRowFrame
            .rectangleForNextRow(0, RHDialogs.evaluationNotesFooterScrollBottomPadding)
            .assignedAsFrameOfView(this._bottomView)

        const finalHeight = Math.min(
            this._bottomView.frame.max.y,
            dialogBounds.height - RHDialogs.evaluationNotesContentInset * 2
        )

        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(finalHeight, 0.5)
    }

}
